/**
 * Adaptive debounce for the Instagram autocomplete (search-targets).
 *
 * Every settled query that misses the cache is a paid StarAPI call on the
 * RapidAPI key Outfame shares with UpGrow. A fixed 300ms window is SHORTER than
 * the gap between keystrokes of a slow (mostly mobile) typist, so it fired a
 * search between nearly every key: on 2026-10-06, 843 of Outfame's 1,419 daily
 * autocomplete misses (59%) were superseded by a longer stem of the same handle
 * within 15s. Waiting 1.5x the typist's own rhythm (capped) fires once they
 * actually pause. Fast typists keep exactly the old window.
 */

export const SEARCH_DEBOUNCE_FLOOR_MS = 300
export const SEARCH_DEBOUNCE_MAX_MS = 1200
export const SEARCH_DEBOUNCE_RHYTHM_FACTOR = 1.5
// Gaps at or above this are pauses (reading, thinking), not typing rhythm.
const RHYTHM_PAUSE_MS = 2000
// Only the most recent gaps describe the current rhythm.
const RHYTHM_SAMPLES = 4

/**
 * Pure. Debounce delay for the keystroke at the END of `keyTimes` (ms
 * timestamps, oldest first): 1.5x the median of the last few inter-key gaps,
 * clamped to [floor, max]. No usable rhythm returns the floor.
 */
export function adaptiveDebounceMs(
    keyTimes: readonly number[],
    floor: number = SEARCH_DEBOUNCE_FLOOR_MS,
    max: number = SEARCH_DEBOUNCE_MAX_MS
): number {
    const gaps: number[] = []
    for (let i = 1; i < keyTimes.length; i++) {
        const gap = keyTimes[i] - keyTimes[i - 1]
        if (gap > 0 && gap < RHYTHM_PAUSE_MS) gaps.push(gap)
    }
    const recent = gaps.slice(-RHYTHM_SAMPLES).sort((a, b) => a - b)
    if (!recent.length) return floor
    const mid = recent.length >> 1
    const median = recent.length % 2 ? recent[mid] : (recent[mid - 1] + recent[mid]) / 2
    return Math.round(Math.min(max, Math.max(floor, median * SEARCH_DEBOUNCE_RHYTHM_FACTOR)))
}

/**
 * Pure. Canonical form of an autocomplete query so equivalent variants share one
 * cache entry (Instagram handles and hashtags are case-insensitive): trims,
 * strips a leading @ or #, collapses whitespace, lowercases.
 */
export function normalizeSearchTerm(raw: string | null | undefined): string {
    return (raw ?? "")
        .trim()
        .replace(/^[@#]+/, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase()
}
